import type { EndpointResponse } from '@rudi/api/schema';

import {
  observeCompletedGoldenInteractions,
  type CompletedGoldenInteraction,
} from './goldenInteraction';

const INGEST_PATH = '/performance/v1/samples';
const PREFERENCES_PATH = '/performance/v1/preferences';
/** Used until the backend reports its environment's rate, and with older backends. */
export const DEFAULT_SAMPLE_RATE = 0.05;
/** A sample waits for an idle moment, so it never competes with the next click. */
const SEND_IDLE_TIMEOUT_MS = 2_000;
const SEND_FALLBACK_DELAY_MS = 200;
const RETAINED_BREACH_SAMPLE_PREFIX = 'breach';
const ORDINARY_SAMPLE_PREFIX = 'sample';
// An automated browser (the perf walk, the e2e tests) marks its samples so the
// backend keeps them out of what users saw (ADR 0076). An older backend stores
// them as ordinary samples, as before.
const AUTOMATED_SAMPLE_PREFIX = 'automated';
let tenantCollectionEnabled = false;
let environmentSampleRate: number | undefined;
const samplingSeed =
  globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}:${Math.random().toString(36)}`;

export interface PerformanceRumPayload {
  interaction_id: string;
  duration_ms: number;
  request_start_offset_ms?: number;
  response_received_offset_ms?: number;
  frontend_work_ms?: number;
  useful_result_offset_ms: number;
  presented_frame_offset_ms: number;
  outcome: 'success';
  app_version: string;
  slo_version: string;
  browser_class: string;
  device_class: string;
  connection_class: string;
  region_class: string;
  cache_state_id: string;
  data_complexity_bucket: 'fixed' | 'typical' | 'large' | 'unknown';
  trace_correlation?: string;
}

export type PerformanceRumTransport = (
  path: string,
  payload: PerformanceRumPayload,
  sampleId: string
) => Promise<void>;

export interface PerformanceRumOptions {
  appVersion?: string;
  sloVersion?: string;
  sampleRate?: number;
  transport?: PerformanceRumTransport;
  regionClass?: string;
  defaultCacheStateId?: string;
  defaultDataComplexityBucket?: 'fixed' | 'typical' | 'large' | 'unknown';
  fieldBudgets?: Record<string, FieldPerformanceBudget>;
}

export interface FieldPerformanceBudget {
  goldP99Ms: number;
  slowerSegmentP99Ms?: number;
  complexityP99Ms?: Partial<Record<'fixed' | 'typical' | 'large', number>>;
}

declare const __RUDI_PERFORMANCE_FIELD_BUDGETS__: Record<string, FieldPerformanceBudget>;
const compiledFieldBudgets =
  typeof __RUDI_PERFORMANCE_FIELD_BUDGETS__ === 'undefined'
    ? {}
    : __RUDI_PERFORMANCE_FIELD_BUDGETS__;

// Set while the tenant preference is still to be read: interactions that
// complete meanwhile wait for it instead of being dropped.
let preferenceKnown: Promise<void> | null = null;
let resolvePreference: (() => void) | null = null;

export function setPerformanceTelemetryCollectionEnabled(enabled: boolean): void {
  tenantCollectionEnabled = enabled;
  resolvePreference?.();
  preferenceKnown = null;
  resolvePreference = null;
}

/**
 * Hold completed interactions until the tenant preference is set, for a
 * preference read that waits for the first screen's data: the first screen's
 * own interaction completes before it.
 */
export function holdPerformanceTelemetryForPreference(): void {
  if (preferenceKnown) return;
  preferenceKnown = new Promise(resolve => {
    resolvePreference = resolve;
  });
}

/** The share of interactions this environment collects, as its backend reports it. */
export function setPerformanceTelemetrySampleRate(rate: number | undefined): void {
  environmentSampleRate =
    rate !== undefined && Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : undefined;
}

type PerformanceTelemetryPreference = Partial<
  EndpointResponse<'/performance/v1/preferences', 'get'>
>;

async function loadApiClient(): Promise<typeof import('../lib/api-client').apiClient> {
  const { apiClient } = await import('../lib/api-client');
  return apiClient;
}

export async function refreshPerformanceTelemetryCollectionPreference(): Promise<boolean> {
  try {
    const apiClient = await loadApiClient();
    const { data } = await apiClient.get<PerformanceTelemetryPreference>(PREFERENCES_PATH);
    const enabled = data.performance_telemetry_enabled === true;
    setPerformanceTelemetryCollectionEnabled(enabled);
    // An older backend sends no rate; the default applies then.
    setPerformanceTelemetrySampleRate(data.performance_telemetry_sample_rate);
    return enabled;
  } catch {
    setPerformanceTelemetryCollectionEnabled(false);
    return false;
  }
}

interface NetworkInformation {
  effectiveType?: string;
}

export function stableSampleValue(interaction: CompletedGoldenInteraction): number {
  const key = `${samplingSeed}:${interaction.interactionId}:${interaction.runId}`;
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 0x1_0000_0000;
}

function browserClass(): string {
  const userAgent = globalThis.navigator?.userAgent?.toLowerCase() ?? '';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('edg/')) return 'edge';
  if (userAgent.includes('chrome') || userAgent.includes('chromium')) return 'chromium';
  if (userAgent.includes('safari')) return 'safari';
  return 'other';
}

function deviceClass(): string {
  return globalThis.innerWidth > 0 && globalThis.innerWidth < 768 ? 'mobile' : 'desktop';
}

function connectionClass(): string {
  const connection = (globalThis.navigator as Navigator & { connection?: NetworkInformation })
    ?.connection;
  return connection?.effectiveType ?? 'unknown';
}

function fieldBudgetMs(
  interaction: CompletedGoldenInteraction,
  fieldBudgets: Record<string, FieldPerformanceBudget>,
  device: string,
  connection: string
): number | undefined {
  const budget = fieldBudgets[interaction.interactionId];
  if (budget === undefined) return undefined;
  const complexity = interaction.dataComplexityBucket;
  if (complexity !== undefined && complexity !== 'unknown') {
    const complexityBudget = budget.complexityP99Ms?.[complexity];
    if (complexityBudget !== undefined) return complexityBudget;
  }
  if (
    budget.slowerSegmentP99Ms !== undefined &&
    (device === 'mobile' || ['slow-2g', '2g', '3g'].includes(connection))
  ) {
    return budget.slowerSegmentP99Ms;
  }
  return budget.goldP99Ms;
}

async function defaultTransport(
  path: string,
  payload: PerformanceRumPayload,
  sampleId: string
): Promise<void> {
  const apiClient = await loadApiClient();
  await apiClient.post(path, payload, {
    headers: { 'Idempotency-Key': sampleId },
  });
}

async function sendAfterForegroundFrame(
  transport: PerformanceRumTransport,
  payload: PerformanceRumPayload,
  sampleId: string
): Promise<void> {
  // Load screen tracking on delivery to keep it out of the startup entry.
  const { afterScreenFrame, hasPendingScreenInteraction } = await import('./screenCompletion');
  if (hasPendingScreenInteraction()) {
    await new Promise<void>(resolve => {
      afterScreenFrame(resolve);
    });
  }
  await transport(INGEST_PATH, payload, sampleId);
}

async function sendWithRetry(
  transport: PerformanceRumTransport,
  payload: PerformanceRumPayload,
  sampleId: string
): Promise<void> {
  try {
    await sendAfterForegroundFrame(transport, payload, sampleId);
  } catch {
    try {
      await sendAfterForegroundFrame(transport, payload, sampleId);
    } catch {
      // Field telemetry must never affect the measured user interaction.
    }
  }
}

function scheduleSend(send: () => void): void {
  if (typeof globalThis.requestIdleCallback === 'function') {
    globalThis.requestIdleCallback(send, { timeout: SEND_IDLE_TIMEOUT_MS });
  } else {
    setTimeout(send, SEND_FALLBACK_DELAY_MS);
  }
}

export function startPerformanceRum(options: PerformanceRumOptions = {}): () => void {
  const buildRate = import.meta.env.VITE_PERFORMANCE_RUM_SAMPLE_RATE;
  const configuredRate =
    options.sampleRate ??
    (buildRate === undefined || buildRate.trim() === '' ? Number.NaN : Number(buildRate));
  const sampleRate = () =>
    Number.isFinite(configuredRate)
      ? Math.min(1, Math.max(0, configuredRate))
      : (environmentSampleRate ?? DEFAULT_SAMPLE_RATE);
  const transport = options.transport ?? defaultTransport;
  const appVersion = options.appVersion ?? import.meta.env.VITE_APP_VERSION ?? '0.1.0';
  const sloVersion = options.sloVersion ?? import.meta.env.VITE_PERFORMANCE_SLO_VERSION;
  const fieldBudgets = options.fieldBudgets ?? compiledFieldBudgets;

  const collect = (interaction: CompletedGoldenInteraction) => {
    if (!tenantCollectionEnabled) return;
    const device = deviceClass();
    const connection = connectionClass();
    const ordinarilySampled = stableSampleValue(interaction) < sampleRate();
    const budgetMs = fieldBudgetMs(interaction, fieldBudgets, device, connection);
    const retainedBreach =
      !ordinarilySampled && budgetMs !== undefined && interaction.durationMs > budgetMs;
    if (!ordinarilySampled && !retainedBreach) return;
    const sampleKind =
      globalThis.navigator?.webdriver === true
        ? AUTOMATED_SAMPLE_PREFIX
        : retainedBreach
          ? RETAINED_BREACH_SAMPLE_PREFIX
          : ORDINARY_SAMPLE_PREFIX;
    const sampleId = `${sampleKind}:${samplingSeed}:${interaction.runId}`;
    const payload: PerformanceRumPayload = {
      interaction_id: interaction.interactionId,
      duration_ms: interaction.durationMs,
      useful_result_offset_ms: interaction.usefulResultOffsetMs,
      presented_frame_offset_ms: interaction.presentedFrameOffsetMs,
      outcome: interaction.outcome,
      app_version: appVersion,
      slo_version: sloVersion,
      browser_class: browserClass(),
      device_class: device,
      connection_class: connection,
      region_class:
        options.regionClass ?? import.meta.env.VITE_PERFORMANCE_REGION_CLASS ?? 'unknown',
      cache_state_id: interaction.cacheStateId ?? options.defaultCacheStateId ?? 'unknown',
      data_complexity_bucket:
        interaction.dataComplexityBucket ?? options.defaultDataComplexityBucket ?? 'unknown',
    };
    if (interaction.traceCorrelation !== undefined) {
      payload.trace_correlation = interaction.traceCorrelation;
    }
    if (interaction.requestStartOffsetMs !== undefined) {
      payload.request_start_offset_ms = interaction.requestStartOffsetMs;
    }
    if (interaction.responseReceivedOffsetMs !== undefined) {
      payload.response_received_offset_ms = interaction.responseReceivedOffsetMs;
    }
    if (interaction.frontendWorkMs !== undefined) {
      payload.frontend_work_ms = interaction.frontendWorkMs;
    }
    scheduleSend(() => void sendWithRetry(transport, payload, sampleId));
  };

  return observeCompletedGoldenInteractions(interaction => {
    if (preferenceKnown) {
      void preferenceKnown.then(() => collect(interaction));
      return;
    }
    collect(interaction);
  });
}
